import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { store } from 'redux/store';
import rootSaga from 'redux/rootSaga';
import { initSentry } from 'utils/sentry';
// eslint-disable-next-line no-unused-vars
import * as serviceWorkerRegistration from 'utils/serviceWorkerRegistration';

import './index.css';
import App from './components/App/App.jsx';

// Initialize Sentry error tracking (production only)
initSentry();

const container = document.getElementById('root');
const root = createRoot(container);

store.runSaga(rootSaga);

root.render(
  <Provider store={store}>
    <HelmetProvider>
      <React.StrictMode>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <App />
        </BrowserRouter>
      </React.StrictMode>
    </HelmetProvider>
  </Provider>
);

// Register service worker for PWA functionality
// Note: Reload loop issue was fixed by adjusting iOS standalone refresh logic
//
// onUpdate fires once a new deployment's service worker has installed and
// is waiting to take over. Without this callback the new worker just sits
// waiting indefinitely - the browser's HTTP cache headers alone can't force
// it to activate, since the service worker's own cache (service-worker.js)
// intercepts fetches before HTTP caching is ever consulted. SKIP_WAITING is
// already handled server-side in public/service-worker.js; sessionStorage
// guards against a reload loop if activation doesn't go as expected.
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    if (!registration.waiting) {
      return;
    }

    const reloadedKey = 'downkount-sw-reloaded';
    if (sessionStorage.getItem(reloadedKey)) {
      return;
    }

    const activateAndReload = () => {
      sessionStorage.setItem(reloadedKey, '1');
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => window.location.reload(),
        { once: true }
      );
    };

    // Don't reload out from under the user mid-session - this is a SPA, so
    // there's no real browser navigation to hook; defer until the tab is
    // backgrounded and becomes visible again (the user stepping away and
    // coming back), the closest equivalent to "next navigation" here.
    if (document.visibilityState === 'hidden') {
      activateAndReload();
      return;
    }

    document.addEventListener('visibilitychange', function onVisible() {
      if (document.visibilityState === 'hidden') {
        document.removeEventListener('visibilitychange', onVisible);
        activateAndReload();
      }
    });
  },
});
